import { sessionToken } from './auth';

/**
 * Connecting a channel, from the browser (#91).
 *
 * A near twin of `signInWithProvider` in `auth.ts` and deliberately a separate
 * one. That function ends with a session token in `localStorage`; this one ends
 * with a verified channel on the server and nothing in the browser worth
 * holding. Sharing an implementation would have meant one function whose result
 * is sometimes a credential, which is the kind of ambiguity that eventually
 * gets a token written somewhere it should not be.
 *
 * What is shared is the contract: open a window, let the flow run first-party
 * to the API inside it, and take the answer from a `postMessage` whose origin,
 * type and nonce are all checked before a word of it is believed. Any page may
 * post to this window.
 */

const BASE = import.meta.env['VITE_API_URL'] ?? 'https://api.goodbuild.cc';

/** The `type` the API's completion page posts. Half of the contract. */
const POPUP_MESSAGE = 'goodbuild:channel-verified';

/** Long enough to read a consent screen and find a password manager. */
const POPUP_TIMEOUT_MS = 300_000;

/** How often to notice a window that was closed without finishing. */
const CLOSED_POLL_MS = 500;

/** A channel, as the platform describes it and the flow renders it. */
export interface VerifiedChannel {
  platform: string;
  /** The username as the platform spells it. */
  handle: string;
  displayName: string;
  channelUrl: string;
  avatarUrl: string | null;
  /** The address this account would take on GOODBUILD, already free. */
  suggestedHandle: string;
}

export type VerifyResult = { ok: true; channel: VerifiedChannel } | { ok: false; message: string };

function nonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function popupFeatures(): string {
  const width = 520;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  return `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no`;
}

/**
 * Runs one channel verification and answers with what the platform said.
 *
 * **The window is opened empty, before anything is awaited, and pointed at the
 * authorize URL once it arrives.** A browser only honours `window.open` while
 * it still attributes the call to the click, and this flow cannot know where to
 * send it until the API has written the request row and answered — which is one
 * `fetch` too many. Opening blank first keeps the user gesture; the alternative
 * is a popup blocker, which on screen looks exactly like the button doing
 * nothing at all.
 *
 * The start call is an authenticated `POST` rather than a navigation for the
 * same reason: the row it writes has to be keyed to whoever is signed in, and a
 * popup cannot carry a bearer token.
 */
export async function verifyChannel(platform: string): Promise<VerifyResult> {
  const authOrigin = new URL(BASE).origin;
  const expected = nonce();

  const popup = window.open('', 'goodbuild-channel', popupFeatures());
  if (!popup) {
    return {
      ok: false,
      message: 'Your browser blocked the window. Allow popups for this site and try again.',
    };
  }

  let url: string;
  try {
    const response = await fetch(`${BASE}/v1/channels/verify/start`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${sessionToken() ?? ''}`,
      },
      credentials: 'omit',
      body: JSON.stringify({ platform, popupOrigin: window.location.origin, popupNonce: expected }),
    });

    const body = (await response.json().catch(() => ({}))) as { url?: string; error?: string };

    if (!response.ok || typeof body.url !== 'string') {
      popup.close();
      /*
       * The API's own sentence, shown as it was written. The one people will
       * actually meet is the 503 — "TikTok verification is not configured on
       * this deployment" — and replacing it with something vaguer would hide
       * the only useful thing on the screen.
       */
      return { ok: false, message: body.error ?? 'That could not be started. Try again.' };
    }

    url = body.url;
  } catch {
    popup.close();
    return { ok: false, message: 'We could not reach GOODBUILD. Check your connection.' };
  }

  popup.location.href = url;

  return new Promise<VerifyResult>((resolve) => {
    let settled = false;

    const settle = (result: VerifyResult): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(poll);
      clearTimeout(timer);
      try {
        if (!popup.closed) popup.close();
      } catch {
        // Still cross-origin on the platform's own page. It closes itself.
      }
      resolve(result);
    };

    function onMessage(event: MessageEvent): void {
      /*
       * Origin, type and nonce, all three. What arrives here decides whose name
       * goes on a creator profile — treating an unchecked message as an answer
       * would let any open tab name somebody else's channel as yours.
       */
      if (event.origin !== authOrigin) return;

      const data = event.data as {
        type?: string;
        nonce?: string;
        error?: string;
        channel?: VerifiedChannel;
      } | null;

      if (data?.type !== POPUP_MESSAGE || data.nonce !== expected) return;

      if (typeof data.error === 'string') {
        settle({ ok: false, message: data.error });
        return;
      }

      if (!data.channel || typeof data.channel.handle !== 'string') return;
      settle({ ok: true, channel: data.channel });
    }

    const poll = setInterval(() => {
      if (popup.closed) {
        settle({
          ok: false,
          message: 'The window closed before it finished. Nothing has changed.',
        });
      }
    }, CLOSED_POLL_MS);

    const timer = setTimeout(
      () => settle({ ok: false, message: 'That took too long. Try again.' }),
      POPUP_TIMEOUT_MS,
    );

    window.addEventListener('message', onMessage);
  });
}
